
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, XCircle, AlertCircle, Shield } from 'lucide-react';
import type { Payment } from '@/types/payments';

interface PaymentStatusProps {
  payment: Payment;
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({ payment }) => {
  const isOverdue = new Date(payment.due_date) < new Date() && payment.status === 'pending';
  const isDepositGuarantee = payment.payment_type === 'deposit_guarantee';

  const getStatusConfig = () => {
    if (payment.status === 'paid') {
      return {
        variant: 'default' as const,
        icon: isDepositGuarantee ? <Shield className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />,
        label: isDepositGuarantee ? 'Empreinte faite' : 'Payé',
        className: 'bg-green-100 text-green-800'
      };
    }
    
    if (payment.status === 'failed') {
      return {
        variant: 'destructive' as const,
        icon: <XCircle className="h-3 w-3" />,
        label: 'Échoué',
        className: 'bg-red-100 text-red-800'
      };
    }
    
    if (payment.status === 'cancelled') {
      return {
        variant: 'secondary' as const,
        icon: <XCircle className="h-3 w-3" />,
        label: 'Annulé',
        className: 'bg-gray-100 text-gray-800'
      };
    }
    
    if (isOverdue) {
      return {
        variant: 'destructive' as const,
        icon: <AlertCircle className="h-3 w-3" />,
        label: 'En retard',
        className: 'bg-red-100 text-red-800'
      };
    }
    
    return {
      variant: 'outline' as const,
      icon: isDepositGuarantee ? <Shield className="h-3 w-3" /> : <Clock className="h-3 w-3" />,
      label: 'En attente',
      className: 'bg-yellow-100 text-yellow-800'
    };
  };

  const config = getStatusConfig();

  return (
    <Badge variant={config.variant} className={`flex items-center gap-1 ${config.className}`}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

export default PaymentStatus;
